/**
 * Device detection + render quality tiers for Afters.
 * Touch devices default to `low`; override with ?quality=low|medium|high.
 */

/** @typedef {"low" | "medium" | "high"} QualityTier */

/** @type {Record<QualityTier, {
 *   lines: number,
 *   crowd: number,
 *   people: number,
 *   texture: number,
 *   bloomKernel: number,
 *   particles: number,
 *   grass: number,
 * }>} */
export const QUALITY = {
  low: {
    lines: 240,
    crowd: 600,
    people: 10,
    texture: 96,
    bloomKernel: 8,
    particles: 0.4,
    grass: 0.3,
  },
  medium: {
    lines: 300,
    crowd: 1e3,
    people: 16,
    texture: 128,
    bloomKernel: 12,
    particles: 0.7,
    grass: 0.6,
  },
  high: {
    lines: 300,
    crowd: 1500,
    people: 22,
    texture: 128,
    bloomKernel: 16,
    particles: 1,
    grass: 1,
  },
};

/** @deprecated mangled alias */
export const Un = QUALITY;

function queryQualityOverride() {
  try {
    const q = new URLSearchParams(location.search).get("quality");
    if (q === "low" || q === "medium" || q === "high") return q;
  } catch {
    /* ignore */
  }
  return null;
}

function isAppleMobileUA() {
  try {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
  } catch {
    return false;
  }
}

/**
 * True when we should show the touch overlay (phones / tablets / coarse pointer).
 * Includes iPad with trackpad (hover may be available but touch UI still needed).
 */
export function isTouchDevice() {
  try {
    if (typeof navigator === "undefined") return false;
    if ((navigator.maxTouchPoints || 0) <= 0 && !isAppleMobileUA()) return false;
    if (isAppleMobileUA()) return true;
    try {
      if (matchMedia("(hover: none) and (pointer: coarse)").matches) return true;
    } catch {
      /* ignore */
    }
    // Fallback: many Android tablets report maxTouchPoints without coarse pointer
    return (navigator.maxTouchPoints || 0) > 1 && matchMedia("(pointer: fine)").matches === false;
  } catch {
    return false;
  }
}

/** @deprecated mangled alias */
export const Wn = isTouchDevice;

/**
 * Pick a quality tier. Query override wins; otherwise touch → low, else GPU heuristic.
 * @returns {QualityTier}
 */
export function pickQuality() {
  const override = queryQualityOverride();
  if (override) return override;
  if (isTouchDevice()) return "low";
  try {
    const gl = document.createElement("canvas").getContext("webgl2");
    if (!gl) return "low";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = String(
      gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER),
    ).toLowerCase();
    if (/swiftshader|llvmpipe|software|basic render/.test(renderer)) return "low";
    if (
      /intel|mali|adreno|powervr/.test(renderer) ||
      (navigator.hardwareConcurrency || 8) <= 4
    ) {
      return "medium";
    }
    return "high";
  } catch {
    return "medium";
  }
}

/** @deprecated mangled alias */
export const Gn = pickQuality;
